import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import {
  BarChart3,
  PieChart,
  Calendar,
  TrendingUp,
  Clock,
  Zap,
  Loader2,
  ChevronDown,
  RefreshCw,
  MessageSquare,
  Users,
  DollarSign,
  Download,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Play
} from "lucide-react";
import {
  fetchCreditBalance,
  fetchUsageSummary,
  fetchUsageHistory,
  fetchAdminUsageSummary,
  fetchAdminDailyUsage,
  fetchAIReportsAnalytics,
  fetchCombinedUsage,
  exportUsageData,
  getCreditStatusColor,
  formatCredits,
  MODE_COLORS,
  MODE_LABELS,
  CREDITS_PER_CHAT
} from "../services/ChatUsageService";

// Safe date formatting helper
const formatDate = (dateValue, options = {}) => {
  if (!dateValue) return "—";
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", options);
};

// Helper to extract numeric value from count (handles both number and object with count/requests/credits)
const extractNumericValue = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'object') {
    if (typeof value.count === 'number') return value.count;
    if (typeof value.requests === 'number') return value.requests;
    if (typeof value.credits === 'number') return value.credits;
    const numericValue = Object.values(value).find(v => typeof v === 'number');
    return numericValue ?? 0;
  }
  return 0;
};

// Simple SVG Pie Chart Component
const SimplePieChart = ({ data, size = 160 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) {
    return (
      <div className="flex items-center justify-center" style={{ width: size, height: size }}>
        <div className="text-slate-500 text-sm">No data</div>
      </div>
    );
  }

  let currentAngle = -90;
  const segments = data.map((item) => {
    const angle = (item.value / total) * 360;
    const startAngle = currentAngle;
    currentAngle += angle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = ((startAngle + angle) * Math.PI) / 180;

    const radius = size / 2 - 10;
    const cx = size / 2;
    const cy = size / 2;

    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);

    const largeArc = angle > 180 ? 1 : 0;

    return {
      ...item,
      path: `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`,
    };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {segments.map((segment, i) => (
        <path
          key={i}
          d={segment.path}
          fill={segment.color}
          className="transition-opacity hover:opacity-80"
        />
      ))}
      <circle cx={size/2} cy={size/2} r={size/4} className="fill-slate-900" />
      <text
        x={size/2}
        y={size/2}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-white text-lg font-bold"
      >
        {total}
      </text>
      <text
        x={size/2}
        y={size/2 + 14}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-slate-400 text-[10px]"
      >
        requests
      </text>
    </svg>
  );
};

// Simple SVG Bar Chart Component with stacked bars support
const SimpleBarChart = ({ data, height = 200, stacked = false }) => {
  const maxValue = stacked
    ? Math.max(...data.map(d => (d.segments || []).reduce((sum, s) => sum + s.value, 0)), 1)
    : Math.max(...data.map(d => d.value), 1);

  const slots = Math.max(data.length, 7);
  const barWidth = Math.min(100 / slots, 12);
  const gap = 2;
  const totalWidth = data.length * (barWidth + gap);
  const startOffset = Math.max((100 - totalWidth) / 2, 5);

  return (
    <div className="w-full" style={{ height }}>
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        {data.map((item, i) => {
          const x = startOffset + i * (barWidth + gap);

          if (stacked && item.segments) {
            const total = item.segments.reduce((sum, s) => sum + s.value, 0);
            let currentY = 82;

            return (
              <g key={i}>
                {item.segments.map((segment, j) => {
                  const segmentHeight = Math.max((segment.value / maxValue) * 70, segment.value > 0 ? 2 : 0);
                  currentY -= segmentHeight;
                  return (
                    <rect
                      key={j}
                      x={x}
                      y={currentY}
                      width={barWidth}
                      height={segmentHeight}
                      fill={segment.color || "#8B5CF6"}
                      className="transition-all hover:opacity-80"
                      rx={j === item.segments.length - 1 ? "2" : "0"}
                    />
                  );
                })}
                {total > 0 && (
                  <text
                    x={x + barWidth / 2}
                    y={82 - (total / maxValue) * 70 - 3}
                    textAnchor="middle"
                    className="fill-slate-400"
                    style={{ fontSize: '5px' }}
                  >
                    {total}
                  </text>
                )}
              </g>
            );
          }

          const barHeight = Math.max((item.value / maxValue) * 70, item.value > 0 ? 2 : 0);
          return (
            <g key={i}>
              <rect
                x={x}
                y={82 - barHeight}
                width={barWidth}
                height={barHeight}
                fill={item.color || "#8B5CF6"}
                className="transition-all hover:opacity-80"
                rx="2"
              />
              {item.value > 0 && (
                <text
                  x={x + barWidth / 2}
                  y={79 - barHeight}
                  textAnchor="middle"
                  className="fill-slate-400"
                  style={{ fontSize: '5px' }}
                >
                  {item.value}
                </text>
              )}
            </g>
          );
        })}
        <line x1="5" y1="83" x2="95" y2="83" stroke="#475569" strokeWidth="0.3" />
      </svg>
      <div className="flex justify-around text-[10px] text-slate-500 mt-1 px-2">
        {data.map((item, i) => (
          <span key={i} className="truncate text-center" style={{ maxWidth: `${100 / data.length}%` }}>
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    PENDING: { icon: AlertCircle, color: 'text-slate-400 bg-slate-500/20', label: 'Pending' },
    RUNNING: { icon: Play, color: 'text-amber-400 bg-amber-500/20', label: 'Running' },
    SUCCEEDED: { icon: CheckCircle, color: 'text-emerald-400 bg-emerald-500/20', label: 'Succeeded' },
    FAILED: { icon: XCircle, color: 'text-red-400 bg-red-500/20', label: 'Failed' },
  };

  const { icon: Icon, color, label } = config[status] || config.PENDING;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// Progress Bar Component
const ProgressBar = ({ value, className = "" }) => (
  <div className={`h-1.5 bg-slate-700 rounded-full overflow-hidden ${className}`}>
    <div
      className="h-full bg-amber-500 rounded-full transition-all duration-300"
      style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
    />
  </div>
);

// Activity Item Component
const ActivityItem = ({ item }) => {
  const isAIReport = item.type === 'ai_report';
  const credits = item.credits || item.credits_charged || CREDITS_PER_CHAT;

  // Color based on type
  const creditColor = isAIReport
    ? item.report_type === 'advanced' ? 'text-purple-400' : 'text-blue-400'
    : 'text-slate-400';

  const dotColor = isAIReport
    ? item.report_type === 'advanced' ? '#8B5CF6' : '#3B82F6'
    : MODE_COLORS[item.mode] || '#6B7280';

  // Delivery badge config
  const getDeliveryBadge = (delivery) => {
    if (delivery === 'ultra') {
      return { label: 'ULTRA FAST', className: 'bg-red-500/20 text-red-400' };
    }
    if (delivery === 'expedited') {
      return { label: 'EXPEDITED', className: 'bg-orange-500/20 text-orange-400' };
    }
    return null;
  };

  const deliveryBadge = isAIReport ? getDeliveryBadge(item.delivery) : null;

  return (
    <div className="flex items-center justify-between py-2.5 px-3 bg-slate-900/50 rounded-lg hover:bg-slate-800/50 transition-colors">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: dotColor }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-200 truncate">
              {item.description || (isAIReport ? `AI Report (${item.report_type || 'Basic'})` : MODE_LABELS[item.mode] || item.mode || 'AI Chat')}
            </span>
            {deliveryBadge && (
              <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${deliveryBadge.className}`}>
                {deliveryBadge.label}
              </span>
            )}
          </div>
          {item.query_preview && (
            <p className="text-xs text-slate-500 truncate mt-0.5">{item.query_preview}</p>
          )}
          {isAIReport && item.status === 'RUNNING' && item.progress !== undefined && (
            <div className="mt-1.5 flex items-center gap-2">
              <ProgressBar value={item.progress} className="flex-1 max-w-[100px]" />
              <span className="text-xs text-amber-400">{item.progress}%</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0 ml-3">
        {isAIReport && item.status && (
          <StatusBadge status={item.status} />
        )}
        <span className={`text-sm font-medium ${creditColor}`}>
          -{credits.toLocaleString()}
        </span>
        <span className="text-xs text-slate-500 min-w-[60px] text-right">
          {item.timestamp_relative || formatDate(item.timestamp || item.created_at, {
            month: "short",
            day: "numeric",
          })}
        </span>
      </div>
    </div>
  );
};

// AI Reports Analytics Card
const AIReportsCard = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700/50 rounded-xl p-5">
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (!data || data.total_reports === 0) {
    return (
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700/50 rounded-xl p-5">
        <h4 className="text-sm font-medium text-slate-200 mb-4 flex items-center gap-2">
          <FileText className="w-4 h-4 text-violet-500" />
          AI Reports Analytics
        </h4>
        <div className="flex flex-col items-center justify-center h-40 text-center">
          <div className="w-14 h-14 rounded-full bg-violet-500/10 flex items-center justify-center mb-3">
            <FileText className="w-7 h-7 text-violet-400/50" />
          </div>
          <p className="text-slate-400 text-sm font-medium">No AI reports yet</p>
          <p className="text-slate-500 text-xs mt-1">Generate reports to see analytics</p>
        </div>
      </div>
    );
  }

  const successRate = data.total_reports > 0
    ? Math.round((data.by_status?.SUCCEEDED || 0) / data.total_reports * 100)
    : 0;

  const avgPerReport = data.total_reports > 0
    ? Math.round(data.total_credits / data.total_reports)
    : 0;

  return (
    <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700/50 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-medium text-slate-200 flex items-center gap-2">
          <FileText className="w-4 h-4 text-violet-500" />
          AI Reports Analytics
        </h4>
        <span className="text-xs text-slate-500">Last 30 days</span>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-slate-900/50 rounded-lg p-3">
          <p className="text-xs text-slate-500">Total Reports</p>
          <p className="text-xl font-bold text-white">{data.total_reports}</p>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3">
          <p className="text-xs text-slate-500">Success Rate</p>
          <p className="text-xl font-bold text-emerald-400">{successRate}%</p>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3">
          <p className="text-xs text-slate-500">Credits Spent</p>
          <p className="text-xl font-bold text-white">{formatCredits(data.total_credits || 0)}</p>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3">
          <p className="text-xs text-slate-500">Avg per Report</p>
          <p className="text-xl font-bold text-white">{formatCredits(avgPerReport)}</p>
        </div>
      </div>

      {/* By Type */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-slate-500 mb-2">BY TYPE</p>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-slate-300">Basic</span>
              </span>
              <span className="text-slate-400">
                {extractNumericValue(data.by_type?.basic)}
                <span className="text-slate-600 text-xs ml-1">
                  ({data.total_reports > 0 ? Math.round(extractNumericValue(data.by_type?.basic) / data.total_reports * 100) : 0}%)
                </span>
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
                <span className="text-slate-300">Advanced</span>
              </span>
              <span className="text-slate-400">
                {extractNumericValue(data.by_type?.advanced)}
                <span className="text-slate-600 text-xs ml-1">
                  ({data.total_reports > 0 ? Math.round(extractNumericValue(data.by_type?.advanced) / data.total_reports * 100) : 0}%)
                </span>
              </span>
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs text-slate-500 mb-2">BY DELIVERY</p>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-300">Standard</span>
              <span className="text-slate-400">{extractNumericValue(data.by_delivery?.standard)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <span className="text-slate-300">Expedited</span>
                <span className="px-1 py-0.5 text-[9px] font-semibold rounded bg-orange-500/20 text-orange-400">FAST</span>
              </span>
              <span className="text-slate-400">{extractNumericValue(data.by_delivery?.expedited)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <span className="text-slate-300">Ultra Fast</span>
                <span className="px-1 py-0.5 text-[9px] font-semibold rounded bg-red-500/20 text-red-400">PRIORITY</span>
              </span>
              <span className="text-slate-400">{extractNumericValue(data.by_delivery?.ultra)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* By Status */}
      <div>
        <p className="text-xs text-slate-500 mb-2">BY STATUS</p>
        <div className="flex flex-wrap gap-3 text-sm">
          <span className="flex items-center gap-1.5 text-emerald-400">
            <CheckCircle className="w-3.5 h-3.5" />
            Succeeded: {extractNumericValue(data.by_status?.SUCCEEDED)}
          </span>
          <span className="flex items-center gap-1.5 text-red-400">
            <XCircle className="w-3.5 h-3.5" />
            Failed: {extractNumericValue(data.by_status?.FAILED)}
          </span>
          <span className="flex items-center gap-1.5 text-amber-400">
            <Play className="w-3.5 h-3.5" />
            Running: {extractNumericValue(data.by_status?.RUNNING)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Date range preset options
const DATE_PRESETS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "This month", days: "month" },
];

const UsageDashboard = ({ userMeta, isAdmin = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ days: 30, label: "Last 30 days" });
  const [showDateDropdown, setShowDateDropdown] = useState(false);

  // User data
  const [creditBalance, setCreditBalance] = useState(null);
  const [usageSummary, setUsageSummary] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [aiReportsData, setAIReportsData] = useState(null);
  const [isLoadingReports, setIsLoadingReports] = useState(false);

  // Admin data
  const [adminSummary, setAdminSummary] = useState(null);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  const getDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();

    if (dateRange.days === "month") {
      start.setDate(1);
    } else {
      start.setDate(start.getDate() - dateRange.days);
    }

    return {
      startDate: start.toISOString().split("T")[0],
      endDate: end.toISOString().split("T")[0],
    };
  }, [dateRange]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { startDate, endDate } = getDateRange();

      if (isAdmin) {
        const [summaryRes, dailyRes] = await Promise.all([
          fetchAdminUsageSummary(startDate, endDate),
          fetchAdminDailyUsage(startDate, endDate),
        ]);
        setAdminSummary(summaryRes);
        setDailyUsage(dailyRes.daily_usage || []);
      } else {
        // Try the combined endpoint first (new API)
        let combinedRes = null;
        try {
          combinedRes = await fetchCombinedUsage(startDate, endDate, 50);
        } catch {
          // Combined API not available, will fall back to individual endpoints
        }

        if (combinedRes) {
          // Use combined response data per backend API spec:
          // {
          //   credits_balance: number,
          //   summary: {
          //     total_credits_used: number,
          //     chat: { total_requests, total_credits, by_mode },
          //     ai_reports: { total_reports, total_credits, by_type, by_delivery, by_status }
          //   },
          //   recent_activity: [...]
          // }
          setCreditBalance({ credits_balance: combinedRes.credits_balance || userMeta?.credit || 0 });

          // Use summary directly from API response
          const summary = combinedRes.summary || {};
          setUsageSummary(summary);

          // Use recent_activity for activity history
          const recentActivity = combinedRes.recent_activity || [];
          setUsageHistory(recentActivity);

          // Extract AI reports data from summary.ai_reports
          const aiReports = summary.ai_reports;
          if (aiReports) {
            setAIReportsData({
              total_reports: extractNumericValue(aiReports.total_reports),
              total_credits: extractNumericValue(aiReports.total_credits),
              by_type: aiReports.by_type || {},
              by_delivery: aiReports.by_delivery || {},
              by_status: aiReports.by_status || {},
            });
          }
        } else {
          // Fallback to individual endpoints
          let balanceRes = null;
          let summaryRes = null;
          let historyRes = null;

          try {
            balanceRes = await fetchCreditBalance();
          } catch {
            // API not available, will use fallback
          }

          try {
            summaryRes = await fetchUsageSummary(startDate, endDate);
          } catch {
            // API not available, will use fallback
          }

          try {
            historyRes = await fetchUsageHistory({ limit: 20 });
          } catch {
            // API not available, will use fallback
          }

          setCreditBalance(balanceRes || { credits_balance: userMeta?.credit || 0 });
          setUsageSummary(summaryRes || { total_requests: 0, total_credits_used: 0, by_mode: {}, chat: { total_requests: 0 }, ai_reports: { total_reports: 0 } });
          setUsageHistory(historyRes?.history || []);

          // Load AI Reports analytics separately
          setIsLoadingReports(true);
          try {
            const reportsRes = await fetchAIReportsAnalytics(startDate, endDate);
            // Normalize the data to ensure counts are numbers
            setAIReportsData({
              total_reports: extractNumericValue(reportsRes?.total_reports),
              total_credits: extractNumericValue(reportsRes?.total_credits),
              by_type: reportsRes?.by_type || {},
              by_delivery: reportsRes?.by_delivery || {},
              by_status: reportsRes?.by_status || {},
            });
          } catch {
            // API not available
            setAIReportsData(null);
          } finally {
            setIsLoadingReports(false);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load usage data:", err);
      setCreditBalance({ credits_balance: userMeta?.credit || 0 });
      setUsageSummary({ total_requests: 0, total_credits_used: 0, by_mode: {}, chat: { total_requests: 0 }, ai_reports: { total_reports: 0 } });
      setUsageHistory([]);
    } finally {
      setIsLoading(false);
    }
  }, [getDateRange, isAdmin, userMeta?.credit]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const { startDate, endDate } = getDateRange();
      const blob = await exportUsageData(format, startDate, endDate);

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `usage-report-${startDate}-to-${endDate}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      setError("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  // Helper to extract numeric value from count (handles both number and object with requests/credits/count)
  const extractCount = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'object') {
      // Handle object with count, requests, or credits
      if (typeof value.count === 'number') return value.count;
      if (typeof value.requests === 'number') return value.requests;
      if (typeof value.credits === 'number') return value.credits;
      // Fallback: try to find any numeric property
      const numericValue = Object.values(value).find(v => typeof v === 'number');
      return numericValue ?? 0;
    }
    return 0;
  };

  const pieChartData = useMemo(() => {
    // Filter out "help" mode - it's free and shouldn't be displayed to customers
    const filterHelpMode = (entries) => entries.filter(([mode]) => mode !== 'help');

    if (isAdmin && adminSummary?.by_mode) {
      return filterHelpMode(Object.entries(adminSummary.by_mode)).map(([mode, count]) => ({
        label: MODE_LABELS[mode] || mode,
        value: extractCount(count),
        color: MODE_COLORS[mode] || "#6B7280",
      }));
    }
    // Use summary.chat.by_mode per API spec: { case: { requests, credits } }
    const byModeData = usageSummary?.chat?.by_mode;
    if (byModeData && Object.keys(byModeData).length > 0) {
      return filterHelpMode(Object.entries(byModeData)).map(([mode, data]) => ({
        label: MODE_LABELS[mode] || mode,
        // data is { requests, credits }, extract requests count
        value: extractCount(data),
        color: MODE_COLORS[mode] || "#6B7280",
      }));
    }
    return [];
  }, [isAdmin, adminSummary, usageSummary]);

  const barChartData = useMemo(() => {
    // Filter out "help" mode - it's free and shouldn't be displayed
    const filterHelpMode = (entries) => entries.filter(([mode]) => mode !== 'help');

    if (isAdmin && dailyUsage.length > 0) {
      return dailyUsage.slice(-14).map((day) => {
        if (day.by_mode) {
          return {
            label: formatDate(day.date, { weekday: "short" }),
            segments: filterHelpMode(Object.entries(day.by_mode)).map(([mode, count]) => ({
              value: extractCount(count),
              color: MODE_COLORS[mode] || "#6B7280",
              mode,
            })),
          };
        }
        return {
          label: formatDate(day.date, { weekday: "short" }),
          value: day.total_requests || 0,
          color: "#8B5CF6",
        };
      });
    }
    if (usageHistory.length > 0) {
      // Check if usageHistory is in by_day format (has date and requests fields)
      const firstItem = usageHistory[0];
      if (firstItem?.date && (firstItem?.requests !== undefined || firstItem?.credits !== undefined)) {
        // Handle by_day format: [{ date, requests, credits }, ...]
        return usageHistory.slice(-7).map((day) => ({
          label: formatDate(day.date, { weekday: "short" }),
          value: day.requests || 0,
          color: "#3B82F6", // Blue for case assistant
        }));
      }

      // Handle individual activity items format
      const byDayAndMode = {};
      usageHistory.forEach((item) => {
        const day = item.created_at?.split("T")[0] || item.timestamp?.split("T")[0] || "Unknown";
        const mode = item.mode || "case_assistant";
        if (!byDayAndMode[day]) {
          byDayAndMode[day] = {};
        }
        byDayAndMode[day][mode] = (byDayAndMode[day][mode] || 0) + 1;
      });

      return Object.entries(byDayAndMode).slice(-7).map(([date, modes]) => ({
        label: formatDate(date, { weekday: "short" }),
        segments: Object.entries(modes).map(([mode, count]) => ({
          value: count,
          color: MODE_COLORS[mode] || "#6B7280",
          mode,
        })),
      }));
    }
    return [];
  }, [isAdmin, dailyUsage, usageHistory]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
      </div>
    );
  }

  const creditStatus = creditBalance ? getCreditStatusColor(creditBalance.credits_balance) : "green";

  // Extract summary data - ensure we get numbers, not objects
  const chatRequests = extractCount(usageSummary?.chat?.total_requests) || extractCount(usageSummary?.total_requests) || 0;
  const aiReportsCount = extractCount(usageSummary?.ai_reports?.total_reports) || extractCount(aiReportsData?.total_reports) || 0;
  const basicReports = extractCount(usageSummary?.ai_reports?.by_type?.basic) || extractCount(aiReportsData?.by_type?.basic) || 0;
  const advancedReports = extractCount(usageSummary?.ai_reports?.by_type?.advanced) || extractCount(aiReportsData?.by_type?.advanced) || 0;

  return (
    <div className="h-full dark:bg-slate-950 bg-[#f7f4ee] flex flex-col overflow-hidden">
      {/* Hero Banner */}
      <div className="relative overflow-hidden px-6 py-10 sm:py-12">
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(120deg, rgba(139, 92, 246, 0.98), rgba(15, 23, 42, 0.95))'
          }}
        />
        <div
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage: 'radial-gradient(circle at 15% 15%, rgba(255,255,255,0.3), transparent 40%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.2), transparent 35%)'
          }}
        />
        <div className="relative max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="text-white">
              <p className="text-sm uppercase tracking-[0.2em] text-white/70">Usage Analytics</p>
              <h2 className="text-3xl sm:text-4xl font-semibold mt-2">
                {isAdmin ? "Platform Usage Overview" : "Your Usage Dashboard"}
              </h2>
              <p className="text-sm sm:text-base text-white/80 mt-2 max-w-xl">
                {isAdmin
                  ? "Monitor platform-wide usage, credits consumption, and user activity."
                  : "Track your credits, chat requests, and AI reports usage."}
              </p>
            </div>
            <div className="w-full lg:w-[360px]">
              <label className="text-xs font-semibold text-white/80 uppercase tracking-[0.2em]">
                Date Range
              </label>
              <div className="relative mt-3">
                <button
                  onClick={() => setShowDateDropdown(!showDateDropdown)}
                  className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white/95 dark:bg-slate-800 border-0 rounded-xl shadow-lg text-slate-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-violet-200"
                >
                  <span className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-slate-400" />
                    {dateRange.label}
                  </span>
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                </button>

                {showDateDropdown && (
                  <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
                    {DATE_PRESETS.map((preset) => (
                      <button
                        key={preset.label}
                        onClick={() => {
                          setDateRange(preset);
                          setShowDateDropdown(false);
                        }}
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${
                          dateRange.label === preset.label
                            ? "text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-slate-700/50 font-medium"
                            : "text-slate-700 dark:text-slate-300"
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-4 text-xs text-white/80">
                <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                  {chatRequests} chat requests
                </span>
                <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                  {aiReportsCount} AI reports
                </span>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={loadData}
                  className="flex-1 px-4 py-2.5 bg-white text-slate-900 hover:bg-white/90 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  Refresh Data
                </button>
                {isAdmin && (
                  <button
                    onClick={() => handleExport("csv")}
                    disabled={isExporting}
                    className="px-4 py-2.5 bg-white/20 text-white hover:bg-white/30 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Download className="w-5 h-5" />
                    Export
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards Section */}
      <div className="px-6 pb-6 -mt-8">
        <div className="px-6 py-6 border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/80 backdrop-blur rounded-2xl shadow-xl space-y-6">

      {/* Stats Cards - Updated with AI Reports */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {isAdmin ? (
          <>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 * 0.05 }}
              className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/20 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 text-violet-400 text-sm mb-1">
                <MessageSquare className="w-4 h-4" />
                Total Requests
              </div>
              <div className="text-2xl font-bold text-white">
                {adminSummary?.total_requests?.toLocaleString() || 0}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 * 0.05 }}
              className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 text-blue-400 text-sm mb-1">
                <Zap className="w-4 h-4" />
                Credits Used
              </div>
              <div className="text-2xl font-bold text-white">
                {formatCredits(adminSummary?.total_credits_used || 0)}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2 * 0.05 }}
              className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 text-emerald-400 text-sm mb-1">
                <Users className="w-4 h-4" />
                Active Users
              </div>
              <div className="text-2xl font-bold text-white">
                {adminSummary?.unique_users?.toLocaleString() || 0}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3 * 0.05 }}
              className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 text-amber-400 text-sm mb-1">
                <DollarSign className="w-4 h-4" />
                Est. Cost
              </div>
              <div className="text-2xl font-bold text-white">
                ${((adminSummary?.total_credits_used || 0) * 0.001).toFixed(2)}
              </div>
            </motion.div>
          </>
        ) : (
          <>
            {/* Credit Balance */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 * 0.05 }}
              className={`bg-gradient-to-br ${
                creditStatus === "red" ? "from-red-500/10 to-rose-500/10 border-red-500/20" :
                creditStatus === "yellow" ? "from-amber-500/10 to-yellow-500/10 border-amber-500/20" :
                "from-emerald-500/10 to-green-500/10 border-emerald-500/20"
              } border rounded-xl p-4`}
            >
              <div className={`flex items-center gap-2 text-sm mb-1 ${
                creditStatus === "red" ? "text-red-400" :
                creditStatus === "yellow" ? "text-amber-400" :
                "text-emerald-400"
              }`}>
                <Zap className="w-4 h-4" />
                Credits Used
              </div>
              <div className="text-2xl font-bold text-white">
                {formatCredits(usageSummary?.total_credits_used || 0)}
              </div>
              <div className="mt-2 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, (usageSummary?.total_credits_used || 0) / (userMeta?.credit || 1000) * 100)}%` }}
                  transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                  className={`h-full rounded-full ${
                    creditStatus === "red" ? "bg-red-500" :
                    creditStatus === "yellow" ? "bg-amber-500" :
                    "bg-emerald-500"
                  }`}
                />
              </div>
            </motion.div>

            {/* Chat Requests */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 * 0.05 }}
              className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/20 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 text-violet-400 text-sm mb-1">
                <MessageSquare className="w-4 h-4" />
                Chat Requests
              </div>
              <div className="text-2xl font-bold text-white">
                {chatRequests.toLocaleString()}
              </div>
            </motion.div>

            {/* AI Reports */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2 * 0.05 }}
              className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 text-blue-400 text-sm mb-1">
                <FileText className="w-4 h-4" />
                AI Reports
              </div>
              <div className="text-2xl font-bold text-white">
                {aiReportsCount}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Basic: {basicReports} | Advanced: {advancedReports}
              </div>
            </motion.div>

            {/* Average Per Day */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3 * 0.05 }}
              className="bg-gradient-to-br from-slate-500/10 to-gray-500/10 border border-slate-500/20 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                <Clock className="w-4 h-4" />
                Avg/Day
              </div>
              <div className="text-2xl font-bold text-white">
                {usageSummary?.avg_requests_per_day?.toFixed(1) || 0}
              </div>
            </motion.div>
          </>
        )}
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Pie Chart - Usage by Mode */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700/50 rounded-xl p-5"
        >
          <h4 className="text-sm font-medium dark:text-slate-200 text-slate-700 mb-4 flex items-center gap-2">
            <PieChart className="w-4 h-4 text-violet-500" />
            Usage by Assistant
          </h4>
          {pieChartData.length > 0 ? (
            <>
              <div className="flex items-center justify-center">
                <SimplePieChart data={pieChartData} size={160} />
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {pieChartData.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-400">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span>{item.label}: {item.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <div className="w-16 h-16 rounded-full bg-violet-500/10 flex items-center justify-center mb-4">
                <MessageSquare className="w-8 h-8 text-violet-400/50" />
              </div>
              <p className="text-slate-400 text-sm font-medium">No usage yet</p>
              <p className="text-slate-500 text-xs mt-1">Start chatting with an assistant to see your usage breakdown</p>
            </div>
          )}
        </motion.div>

        {/* AI Reports Analytics Card */}
        {!isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <AIReportsCard data={aiReportsData} isLoading={isLoadingReports} />
          </motion.div>
        )}

        {/* Bar Chart - Daily Usage (Admin only or if no AI reports card) */}
        {isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700/50 rounded-xl p-5"
          >
            <h4 className="text-sm font-medium dark:text-slate-200 text-slate-700 mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-violet-500" />
              Daily Activity
            </h4>
            {barChartData.length > 0 ? (
              <>
                <SimpleBarChart data={barChartData} height={160} stacked={true} />
                <div className="flex flex-wrap justify-center gap-4 mt-3">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MODE_COLORS.case_assistant }} />
                    <span>Case Assistant</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MODE_COLORS.marketing }} />
                    <span>Marketing</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mb-4">
                  <TrendingUp className="w-8 h-8 text-blue-400/50" />
                </div>
                <p className="text-slate-400 text-sm font-medium">No activity yet</p>
                <p className="text-slate-500 text-xs mt-1">Your daily chat activity will appear here</p>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Recent Activity - Updated with timestamps and status badges */}
      {!isAdmin && usageHistory.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 border border-slate-700 rounded-xl p-4"
        >
          <h4 className="text-sm font-medium text-slate-300 mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-violet-400" />
            Recent Activity
          </h4>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {usageHistory
              .filter(item => item.mode !== 'help') // Help is free - don't show in activity
              .slice(0, 15)
              .map((item, i) => (
                <ActivityItem key={item.id || i} item={item} />
              ))}
          </div>
        </motion.div>
      )}

      {/* Credits Per Chat Info */}
      <div className="text-center text-xs text-slate-500 pt-4">
        Each chat request costs {CREDITS_PER_CHAT} credits • AI Reports: Basic (1,000) | Advanced (2,000)
      </div>

        </div>
      </div>
    </div>
  );
};

export default UsageDashboard;
